import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { X, Flag, Loader2, Send, Camera, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MillieAvatar } from "./MascotBubble";

export const CATEGORIES = [
  { id: "millie", label: "Millie said something off" },
  { id: "meal", label: "A meal or recipe" },
  { id: "shopping", label: "Shopping list or prices" },
  { id: "bug", label: "Something's broken" },
  { id: "other", label: "Something else" },
];

export default function ReportSheet({ open, onClose, category: preset, context, title = "Report a problem" }) {
  const { activeId } = useAuth();
  const location = useLocation();
  const [category, setCategory] = useState(preset || "bug");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [shot, setShot] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  if (!open) return null;

  const attach = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/reports/screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShot({ ...data, preview: URL.createObjectURL(f) });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't attach that"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (message.trim().length < 3) { toast.error("Tell me a little more so I can sort it"); return; }
    setSending(true);
    try {
      await api.post("/reports", { category, message: message.trim(), page: location.pathname, context: context || {}, household_id: activeId, screenshot_id: shot?.id || null });
      toast.success("Thanks legend — Nikki's got it and will take a look");
      setMessage(""); setShot(null); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-sorted-ink/40 backdrop-blur-sm flex items-end justify-center" onClick={onClose} data-testid="report-sheet">
      <div className="w-full max-w-md bg-[#FFF6F1] rounded-t-[2rem] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <MillieAvatar size={44} float={false} mood="thinking" />
          <div className="flex-1">
            <p className="font-hand text-2xl text-sorted-ink leading-none">{title}</p>
            <p className="text-sm font-semibold text-sorted-slate mt-1">No judgement — every report makes Sorted better.</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="report-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-soft"><X size={18} strokeWidth={2.5} /></button>
        </div>
        {context?.label && <p className="mt-4 text-xs font-bold text-sorted-slate bg-white rounded-2xl px-3 py-2 truncate" data-testid="report-context">About: {context.label}</p>}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="report-categories">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`report-cat-${c.id}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${category === c.id ? "bg-sorted-rose border-sorted-rose text-white" : "bg-white border-[#FDECEF] text-sorted-ink"}`}>{c.label}</button>
          ))}
        </div>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={2000} data-testid="report-message"
          placeholder="What happened? What did you expect instead?"
          className="mt-4 w-full rounded-2xl border-2 border-[#FDECEF] bg-white px-4 py-3 text-sm font-medium text-sorted-ink focus:outline-none focus:border-sorted-rose" />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={attach} data-testid="report-screenshot-input" />
        {shot ? (
          <div className="mt-3 flex items-center gap-3 bg-white rounded-2xl p-2 border-2 border-[#FDECEF]" data-testid="report-screenshot-preview">
            <img src={shot.preview} alt="Screenshot" className="w-14 h-14 object-cover rounded-xl" />
            <span className="flex-1 text-xs font-bold text-sorted-ink">Screenshot attached</span>
            <button onClick={() => setShot(null)} aria-label="Remove screenshot" data-testid="report-screenshot-remove" className="w-9 h-9 rounded-full bg-muted flex items-center justify-center"><Trash2 size={15} strokeWidth={2.5} /></button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="report-screenshot-btn"
            className="mt-3 w-full h-11 rounded-full bg-white border-2 border-dashed border-[#F6C9D3] text-sorted-ink text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} strokeWidth={2.5} />} {uploading ? "Attaching…" : "Add a screenshot (optional)"}
          </button>
        )}
        <button onClick={submit} disabled={sending || uploading} data-testid="report-submit"
          className="mt-4 w-full h-12 rounded-full bg-sorted-ink text-white font-heading font-semibold flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98] transition-transform">
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} strokeWidth={2.5} />} Send to Nikki
        </button>
      </div>
    </div>
  );
}

export function ReportFlag({ onClick, testId, className = "" }) {
  return (
    <button onClick={onClick} aria-label="Report a problem" data-testid={testId}
      className={`inline-flex items-center gap-1 text-[11px] font-bold text-sorted-slate/70 hover:text-sorted-rose transition-colors ${className}`}>
      <Flag size={12} strokeWidth={2.5} /> Report
    </button>
  );
}
